import React, { Component } from 'react';
import { BlessLink } from '../routes';


/* This component is used like the <img>-tag, but inlines the SVG src. There
 * are a couple of reasons why you might want to do this:
 *
 *  - Your SVG wants to use styles defined in our main CSS file. This is not
 *    possible with an <img> SVG because the SVG document lives in a completely
 *    isolated DOM document.
 *
 *  - Your SVG contains links like <a xlink:href="...">...</a>. This component
 *    wraps these using the <BlessLink> component which solves problems with
 *    "naked links" (it prevents page reloads, and potentially rewrites the
 *    URL)
 *
 * Caveats:
 *
 *  - Don't use with external/untrusted SVGs; <img>-tags isolate/sandbox their
 *    src documents; probably for a good reason!
 *
 *  - This component loads the SVG (via XHR) and parses it. During render, it
 *    converts the document into React.createElement()-calls. This conversion
 *    requires adhering to a lot of React-isms, and it's very likely I haven't
 *    addressed all of them; I've simply hacked on this until it works on the
 *    SVGs we have :-)
 */

class InlineSVG extends Component {
	constructor(props) {
		super(props);
		this.state = {serial: 0};
		this.doc = null;
		this.xhr = null;
	}

	abort() {
		if (this.xhr) this.xhr.abort();
		this.xhr = null;
		this.doc = null;
	}

	fetch() {
		this.abort();
		const xhr = this.xhr = new XMLHttpRequest();
		xhr.withCredentials = true;
		xhr.responseType = "text";
		xhr.onreadystatechange = () => {
			if (xhr.readyState !== 4) return;
			if (Math.floor(xhr.status / 100) === 2) {
				var parser = new DOMParser();
				this.doc = parser.parseFromString(xhr.response, 'image/svg+xml')
				this.setState((prev_state) => ({serial: prev_state.serial + 1}));
			} else {
				throw new Error(`GET ${this.props.src} failed; status=${xhr.status}`);
			}
		};
		xhr.open("GET", this.props.src);
		xhr.send();
	}

	componentDidMount() {
		this.fetch();
	}

	componentWillUnmount() {
		this.abort();
	}

	componentDidUpdate(prev_props, prev_state) {
		if (this.props.src !== prev_props.src) this.fetch();
	}

	render() {
		if (this.state.serial === 0) return this.props.loading_component || null;

		var render_rec; render_rec = (node, key, inject_props) => {
			var children = [];
			var props = {key, ...(inject_props || {})};

			for (const attribute of node.attributes) {
				const map_attribute = (attribute) => {
					var name = attribute.name;

					/* convert e.g. "xlink:href" to "href" */
					var xs = name.split(":");
					name = xs[xs.length-1];

					/* convert "class" => "className" */
					if (name === "class") name = "className";

					/* convert stuff like "clip-path" to "clipPath" */
					name = name.replace(/-./g, c => c.slice(1).toUpperCase());

					var value = attribute.value;

					/* style attribute must be objects in React, not strings */
					if (name === "style") {
						const parse_style = (styles) => {
							return styles
								.split(';')
								.filter(style => style.split(':')[0] && style.split(':')[1])
								.map(style => [
									style.split(':')[0].trim().replace(/-./g, c => c.slice(1).toUpperCase()),
									style.split(':')[1].trim()
								])
								.reduce((styleObj, style) => ({
									...styleObj,
									[style[0]]: style[1],

								}), {});
						};
						value = parse_style(value);
					}

					return [name, value];
				};
				const [name, value] = map_attribute(attribute);
				props[name] = value;
			}

			for (var i = 0; i < node.children.length; i++) {
				children.push(render_rec(node.children[i], key + '_' + i));
			}

			var element = React.createElement(node.tagName, props, children);

			if (node.tagName === "a") {
				element = React.createElement(BlessLink, {key}, element);
			}

			return element;
		};

		return render_rec(this.doc.documentElement, '', this.props);
	}
}

export default InlineSVG;



// WEBPACK FOOTER //
// ./src/c/InlineSVG.js